import { useState, useMemo } from 'react';
import { apiClient } from '../../../lib/apiClient';
import { useAuth } from '../../../hooks/useAuth';
import type { User } from '../types';
import {
  useReconcileUserMutation,
  useForgetUserMutation,
  useResetPasswordMutation,
  useSendPasswordResetEmailMutation,
} from '../data/useUserActionMutations';

// C2FO domains that require special handling for password reset
const C2FO_DOMAINS = ['@c2fo.com', '@c2fo.io'];
const C2FO_PASSWORD_RESET_URL = 'https://sh.c2fo.com';

interface UseUserActionsOptions {
  authServiceUuid: string | undefined;
  user: User | null;
}

interface UseUserActionsReturn {
  impersonating: boolean;
  reconciling: boolean;
  sendingPasswordReset: boolean;
  resettingPassword: boolean;
  forgettingUser: boolean;
  successMessage: string | null;
  errorMessage: string | null;
  clearMessages: () => void;
  impersonateUser: () => Promise<void>;
  reconcileUser: () => Promise<void>;
  sendPasswordResetEmail: () => Promise<void>;
  resetPassword: (newPassword: string) => Promise<void>;
  forgetUser: () => Promise<void>;
  // Feature gate status
  canResetPassword: boolean;
  // Special flags for C2FO users
  isC2foUser: boolean;
  c2foPasswordResetUrl: string;
  // Action availability based on user state
  canSendPasswordResetEmail: boolean;
  canReconcile: boolean;
}

/**
 * Hook for managing user actions in the Admin UI
 *
 * Provides all user management actions with proper feature gating and business logic checks.
 * All actions call the User API directly through the gateway.
 *
 * @param options - Configuration options
 * @param options.authServiceUuid - The auth service UUID of the user
 * @param options.user - The user object
 * @returns User action functions and state
 *
 * @example
 * ```typescript
 * const {
 *   resetPassword,
 *   canResetPassword,
 *   isC2foUser,
 *   c2foPasswordResetUrl
 * } = useUserActions({ userId, user });
 *
 * // Check if action is allowed before showing UI
 * if (canResetPassword) {
 *   if (isC2foUser) {
 *     // Show link to external reset URL
 *   } else {
 *     // Show reset password modal
 *   }
 * }
 * ```
 */
export const useUserActions = ({ authServiceUuid, user }: UseUserActionsOptions): UseUserActionsReturn => {
  const [impersonating, setImpersonating] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const { canResetPassword } = useAuth();

  // Mutations
  const reconcileMutation = useReconcileUserMutation();
  const forgetMutation = useForgetUserMutation();
  const resetPasswordMutation = useResetPasswordMutation();
  const sendPasswordResetMutation = useSendPasswordResetEmailMutation();

  // Check if user is from C2FO domain
  const isC2foUser = useMemo(() => {
    if (!user?.emailAddress) return false;
    return C2FO_DOMAINS.some((domain) => user.emailAddress.toLowerCase().includes(domain.toLowerCase()));
  }, [user?.emailAddress]);

  // Check if user can send password reset email (based on registration status)
  const canSendPasswordResetEmail = useMemo(() => {
    // User must be registered (status > 0, i.e., not NEW)
    if (!user || user.status === undefined) return false;
    const status = typeof user.status === 'string' ? parseInt(user.status, 10) : user.status;
    return status > 0; // Status must be greater than 0 (NEW)
  }, [user]);

  // Check if user can be reconciled (based on datacenter count)
  const canReconcile = useMemo(() => {
    if (!user || !user.datacenters) return false;
    return user.datacenters.length > 1;
  }, [user]);

  const clearMessages = () => {
    setSuccessMessage(null);
    setErrorMessage(null);
  };

  const impersonateUser = async () => {
    if (!authServiceUuid) return;

    setImpersonating(true);
    setErrorMessage(null);
    try {
      const response = await apiClient()
        .post('api/users/user-source/subject-token-login-url', {
          json: { uuid: authServiceUuid },
        })
        .json<{ message: string }>();

      window.open(response.message, '_blank');
      setSuccessMessage('Impersonation session opened in new tab');
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to impersonate user';
      setErrorMessage(errorMsg);
    } finally {
      setImpersonating(false);
    }
  };

  const reconcileUser = async () => {
    if (!authServiceUuid) return;

    // Check if user can be reconciled
    if (!canReconcile) {
      setErrorMessage('User must have multiple datacenters to reconcile');
      return;
    }

    clearMessages();
    try {
      await reconcileMutation.mutateAsync({ authServiceUuid });
      setSuccessMessage('User reconcile requested. Refresh in a minute to see if reconcile was successful.');
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to reconcile user';
      setErrorMessage(`Failed to reconcile user: ${errorMsg}`);
    }
  };

  const sendPasswordResetEmail = async () => {
    if (!authServiceUuid || !user) return;

    // Check if password reset email can be sent
    if (!canSendPasswordResetEmail) {
      setErrorMessage('Password reset email cannot be sent for unregistered users');
      return;
    }

    clearMessages();
    try {
      await sendPasswordResetMutation.mutateAsync({ user });
      setSuccessMessage(`User password reset email sent to ${user.emailAddress}`);
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to send password reset email';
      setErrorMessage(`Failed to send password reset email: ${errorMsg}`);
    }
  };

  const resetPassword = async (newPassword: string) => {
    if (!authServiceUuid) return;

    // Check feature gate
    if (!canResetPassword) {
      setErrorMessage('You do not have permission to reset passwords');
      return;
    }

    clearMessages();
    try {
      await resetPasswordMutation.mutateAsync({ authServiceUuid, password: newPassword });
      setSuccessMessage('Password reset successfully');
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to reset password';
      setErrorMessage(`Failed to reset password: ${errorMsg}`);
      throw err; // Re-throw for modal handling
    }
  };

  const forgetUser = async () => {
    if (!authServiceUuid) return;

    clearMessages();
    try {
      await forgetMutation.mutateAsync({ authServiceUuid });
      setSuccessMessage('User forgotten successfully');
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to forget user';
      setErrorMessage(`Failed to forget user: ${errorMsg}`);
    }
  };

  return {
    impersonating,
    reconciling: reconcileMutation.isPending,
    sendingPasswordReset: sendPasswordResetMutation.isPending,
    resettingPassword: resetPasswordMutation.isPending,
    forgettingUser: forgetMutation.isPending,
    successMessage,
    errorMessage,
    clearMessages,
    impersonateUser,
    reconcileUser,
    sendPasswordResetEmail,
    resetPassword,
    forgetUser,
    canResetPassword,
    isC2foUser,
    c2foPasswordResetUrl: C2FO_PASSWORD_RESET_URL,
    canSendPasswordResetEmail,
    canReconcile,
  };
};
